/**
 * How Customer 360 reads a customer's own identity off the `getCustomer` slice.
 *
 * Shared because it was not: the page header composed the name from `first_name`/`last_name` when
 * `full_name` was empty, while Section 1 read `full_name` directly and fell through to the frame's
 * dummy. The same customer therefore rendered as `RV D` in one place and `Priya Sharma` in the
 * other. One reader, one answer. PLA-8828.
 */
import moment from 'moment';

export interface ICustomerIdentity {
  id?: number | string | null;
  customer_id?: string | null;
  alternate_id?: string | null;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  state?: string | null;
  joined_at?: string | null;
}

export const ACTIVE_STATE = 'active';

/**
 * `full_name` is not always populated — the UAT record `RV D` carries first and last names and an
 * empty `full_name` — so composing is the fallback, not the other way round. Returns an empty
 * string rather than a placeholder: a customer whose name cannot be read must render as absent, not
 * as somebody else.
 */
export const customerDisplayName = (customer?: ICustomerIdentity | null): string => {
  if (!customer) {
    return '';
  }

  if (customer.full_name) {
    return customer.full_name;
  }

  return [customer.first_name, customer.last_name].filter(Boolean).join(' ');
};

export const isCustomerActive = (customer?: ICustomerIdentity | null): boolean =>
  customer?.state === ACTIVE_STATE;

/**
 * How long this customer has been on the tenant, written as the frame writes it (`2y 1m`). Shared
 * for the same reason the name reader is: Section 1's Identity grid and Section 2's Habit Evidence
 * both state an observed tenure, and two readers of one `joined_at` would eventually disagree.
 * Empty rather than a placeholder when the date is missing or in the future. PLA-8828.
 */
export const tenureText = (joinedAt?: string | null): string => {
  if (!joinedAt) {
    return '';
  }

  const months = moment().diff(moment(joinedAt), 'months');

  return months < 0 ? '' : `${Math.floor(months / 12)}y ${months % 12}m`;
};
